import React from 'react';
import PropTypes from 'prop-types';
import { formatCurrency } from '../../../utils/formatting';
import {
    activeQuotesFor, calcMarginTone, carryingRatePct, grossMarginGood, marginCalc,
} from '../../../utils/unitIntelligence';
import { useDealershipSettings } from '../../../contexts/settingsContext';
import './IronSightCards.css';

const CALC_COLORS = { good: '#15803d', warn: '#b45309', neg: '#b91c1c' };

function CarryingCostSection({ unit, calcPrice, setCalcPrice, pipeline, quoteEquipment }) {
    // The dealership's floor plan APR and margin floor (7% / 10% by default).
    const { aprPct, marginFloorPct } = useDealershipSettings();
    if (unit.GrossMargin == null) return null;
    const marginColor = grossMarginGood(unit.GrossMarginPct, marginFloorPct) ? '#15803d' : '#b45309';

    // Shared with the full unit page (utils/unitIntelligence).
    const activeQuotes = activeQuotesFor(pipeline, quoteEquipment);
    const {
        price, calcGross, calcGrossPct, calcRemaining, holdAtSale, higherQuotes,
    } = marginCalc(unit, calcPrice, activeQuotes);

    return (
        <section className="detail-section is-card">
            <div className="is-card-body">
            <div className="section-heading-row" style={{ borderBottom: 'none' }}>
                <h3 className="is-section-title" style={{ marginBottom: 0 }}>Estimated Carrying Cost</h3>
                <span className="info-tooltip">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15" className="info-icon">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span className="tooltip-text">
                        {unit.StockStatus === 'P' ? (
                            <>
                                <strong>Pending unit — not yet on books.</strong><br />
                                Gross Margin = Best Sell Price − Cost Estimate<br />
                                (Cost Estimate = Sales Book Value + planned reconditioning)<br />
                                Carrying cost is $0 — no money has been committed yet.
                            </>
                        ) : (
                            <>
                                <strong>In-Stock unit — on the books.</strong><br />
                                Gross Margin = Best Sell Price − True Cost<br />
                                True Cost = Wholesale + Retail + Access + After-Market + Charge + Option + Other + Paint + Pre-Delivery + Repair + Surcharge + Trim<br />
                                Daily Carrying Cost = True Cost × {carryingRatePct(unit, aprPct)}% APR ÷ 365
                            </>
                        )}
                    </span>
                </span>
            </div>

            {/* Gross margin formula */}
            <div className="fp-formula">
                <span className="fp-formula-item">
                    <span className="fp-formula-label">Asking Price</span>
                    <span className="fp-formula-amount">{formatCurrency(unit.Best_Sell_Price)}</span>
                </span>
                <span className="fp-formula-op">−</span>
                <span className="fp-formula-item">
                    <span className="fp-formula-label">{unit.StockStatus === 'P' ? 'Cost Estimate' : 'True Cost'}</span>
                    <span className="fp-formula-amount">{formatCurrency(unit.CarryingCostBasis)}</span>
                </span>
                <span className="fp-formula-op">=</span>
                <span className="fp-formula-item">
                    <span className="fp-formula-label">Gross Margin</span>
                    <span className="fp-formula-amount" style={{ color: marginColor }}>
                        {formatCurrency(unit.GrossMargin)}
                    </span>
                    <span className="fp-formula-pct" style={{ color: marginColor }}>
                        {unit.GrossMarginPct?.toFixed(1)}%
                    </span>
                </span>
            </div>

            <div className="fp-grid">
                <div className="fp-card">
                    <span className="fp-label">Daily Carrying Cost</span>
                    {unit.StockStatus === 'P' ? (
                        <>
                            <span className="fp-value" style={{ color: '#15803d' }}>$0</span>
                            <span className="fp-sub">Not yet on books</span>
                        </>
                    ) : (
                        <>
                            <span className="fp-value">
                                {formatCurrency(unit.CarryingCostDaily)}
                                <span className="fp-per-day">/day</span>
                            </span>
                            <span className="fp-sub">
                                {carryingRatePct(unit, aprPct)}% APR on {formatCurrency(unit.CarryingCostBasis)}
                            </span>
                        </>
                    )}
                </div>
                <div className="fp-card">
                    <span className="fp-label">Carrying Cost to Date</span>
                    {unit.StockStatus === 'P' ? (
                        <>
                            <span className="fp-value" style={{ color: '#15803d' }}>$0</span>
                            <span className="fp-sub">{unit.DaysInStock} days in stock</span>
                        </>
                    ) : (
                        <>
                            <span className="fp-value fp-value-warn">{formatCurrency(unit.CarryingCostToDate)}</span>
                            <span className="fp-sub">{unit.DaysInStock} days in stock</span>
                        </>
                    )}
                </div>
                {unit.CarryingCostProjected != null && (
                    <div className="fp-card">
                        <span className="fp-label">Projected to Sale</span>
                        <span className="fp-value fp-value-danger">{formatCurrency(unit.CarryingCostProjected)}</span>
                        <span className="fp-sub">~{Math.round(unit.MedianDays)}d more to sell</span>
                    </div>
                )}
                {unit.CarryingCostTotalExposure != null && (
                    <div className="fp-card fp-card-total">
                        <span className="fp-label">Total Exposure</span>
                        <span className="fp-value fp-value-danger">{formatCurrency(unit.CarryingCostTotalExposure)}</span>
                        <span className="fp-sub">{unit.DaysInStock + Math.round(unit.MedianDays)} total days</span>
                    </div>
                )}
                {unit.MarginAfterCarrying != null && unit.StockStatus === 'S' && (
                    <div className="fp-card fp-card-highlight">
                        <span className="fp-label">Remaining Margin Today</span>
                        <span className="fp-value" style={{ color: unit.MarginAfterCarrying >= 0 ? '#15803d' : '#b91c1c' }}>
                            {formatCurrency(unit.MarginAfterCarrying)}
                        </span>
                        <span className="fp-sub">
                            After {formatCurrency(unit.CarryingCostToDate)} carrying to date
                            {unit.MarginAfterCarryingPct != null && ` · ${unit.MarginAfterCarryingPct.toFixed(1)}%`}
                        </span>
                    </div>
                )}
                {unit.MarginAtSale != null && unit.StockStatus === 'S' && (
                    <div className="fp-card fp-card-highlight">
                        <span className="fp-label">Margin at Predicted Sale Date</span>
                        <span className="fp-value" style={{ color: unit.MarginAtSale >= 0 ? '#15803d' : '#b91c1c' }}>
                            {formatCurrency(unit.MarginAtSale)}
                        </span>
                        <span className="fp-sub">
                            After {formatCurrency(unit.CarryingCostTotalExposure)} total exposure
                            {unit.MarginAtSalePct != null && ` · ${unit.MarginAtSalePct.toFixed(1)}%`}
                        </span>
                    </div>
                )}
            </div>

            {/* Interactive Margin Calculator */}
            {unit.CarryingCostBasis != null && (
                <div className="margin-calculator">
                    <div className="margin-calc-header">
                        <div className="margin-calc-title-row">
                            <span className="margin-calc-title">Margin Calculator</span>
                            <span className="info-tooltip">
                                <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" className="info-icon">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                <span className="tooltip-text tooltip-text-left">
                                    <strong>How this is calculated</strong><br /><br />
                                    <strong>Gross Margin</strong><br />
                                    Sale Price − True Cost ({formatCurrency(unit.CarryingCostBasis)})<br />
                                    Changes as you adjust the price.<br /><br />
                                    <strong>If accepted today</strong><br />
                                    Gross Margin − {formatCurrency(unit.CarryingCostToDate)} already accrued<br />
                                    ({unit.DaysInStock} days × {formatCurrency(unit.CarryingCostDaily)}/day)<br />
                                    This is the net you walk away with if you take the offer right now.<br /><br />
                                    {holdAtSale != null && (<>
                                        <strong>If held to predicted sale</strong><br />
                                        Always based on original asking price ({formatCurrency(unit.Best_Sell_Price)})<br />
                                        Original Gross Margin − Total Exposure ({formatCurrency(unit.CarryingCostTotalExposure)})<br />
                                        = {formatCurrency(unit.CarryingCostToDate)} accrued + {formatCurrency(unit.CarryingCostProjected)} projected (~{Math.round(unit.MedianDays)}d more)<br />
                                        Use this to decide if the offer is better than waiting.
                                    </>)}
                                </span>
                            </span>
                        </div>
                        <span className="margin-calc-hint">
                            True cost: {formatCurrency(unit.CarryingCostBasis)} · Carrying: {formatCurrency(unit.CarryingCostToDate)} accrued
                        </span>
                    </div>
                    <div className="margin-calc-input-row">
                        <label htmlFor="calc-price" className="margin-calc-label">Sale Price</label>
                        <div className="margin-calc-input-wrap">
                            <span className="margin-calc-prefix">$</span>
                            <input
                                id="calc-price"
                                type="number"
                                className="margin-calc-input"
                                value={price}
                                min={0}
                                step={1000}
                                onChange={(e) => setCalcPrice(Number(e.target.value))}
                            />
                        </div>
                        {calcPrice !== null && (
                            <button className="margin-calc-reset" onClick={() => setCalcPrice(null)}>
                                Reset
                            </button>
                        )}
                    </div>
                    <div className="margin-calc-results">
                        <div className="margin-calc-row">
                            <span className="margin-calc-result-label">
                                Gross Margin
                                <span className="margin-calc-sublabel"> (before carrying cost)</span>
                            </span>
                            <span className="margin-calc-result-value" style={{ color: CALC_COLORS[calcMarginTone(calcGross, calcGrossPct, marginFloorPct)] }}>
                                {formatCurrency(calcGross)}
                                <span className="margin-calc-pct"> ({calcGrossPct.toFixed(1)}%)</span>
                            </span>
                        </div>
                        {unit.CarryingCostToDate != null && (
                            <div className="margin-calc-row margin-calc-row-primary">
                                <span className="margin-calc-result-label">
                                    If accepted today
                                    <span className="margin-calc-sublabel"> (after {formatCurrency(unit.CarryingCostToDate)} accrued)</span>
                                </span>
                                <span className="margin-calc-result-value" style={{ color: calcRemaining >= 0 ? '#15803d' : '#b91c1c' }}>
                                    {formatCurrency(calcRemaining)}
                                </span>
                            </div>
                        )}
                        {holdAtSale != null && (
                            <div className="margin-calc-row margin-calc-row-secondary">
                                <span className="margin-calc-result-label">
                                    If held to predicted sale
                                    <span className="margin-calc-sublabel"> (~{Math.round(unit.MedianDays)}d more at {formatCurrency(unit.Best_Sell_Price)})</span>
                                </span>
                                <span className="margin-calc-result-value" style={{ color: holdAtSale >= 0 ? '#15803d' : '#b91c1c' }}>
                                    {formatCurrency(holdAtSale)}
                                </span>
                            </div>
                        )}
                    </div>
                    {calcPrice != null && higherQuotes.length > 0 && (
                        <div className="quote-aware-warning">
                            <div className="quote-aware-header">
                                <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" style={{ color: '#b45309', flexShrink: 0 }}>
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <span>{higherQuotes.length} active quote{higherQuotes.length !== 1 ? 's' : ''} at or above {formatCurrency(calcPrice)}</span>
                            </div>
                            <div className="quote-aware-list">
                                {higherQuotes.map((q, i) => (
                                    <div key={q.quote_id || i} className="quote-aware-item">
                                        <span className="quote-aware-price">{formatCurrency(q.selling_price)}</span>
                                        {q.buyer_name && <span className="quote-aware-buyer">{q.buyer_name}</span>}
                                        {q.salesperson && <span className="quote-aware-sales">{q.salesperson}</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {calcPrice != null && activeQuotes.length > 0 && higherQuotes.length === 0 && (
                        <div className="quote-aware-info">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" style={{ color: '#15803d', flexShrink: 0 }}>
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span>No active quotes at or above {formatCurrency(calcPrice)}</span>
                        </div>
                    )}
                </div>
            )}
            </div>
        </section>
    );
}

CarryingCostSection.propTypes = {
    unit: PropTypes.object.isRequired,
    calcPrice: PropTypes.number,
    setCalcPrice: PropTypes.func.isRequired,
    pipeline: PropTypes.object,
    quoteEquipment: PropTypes.object,
};

CarryingCostSection.defaultProps = {
    calcPrice: null,
    pipeline: null,
    quoteEquipment: null,
};

export default CarryingCostSection;
